import { useMemo } from 'react';

import { useNavigate } from 'react-router-dom';

import { ASSET_TYPES } from '@/shared/api/openmetadata';
import { mapTagsToBasicInfo } from '@/shared/lib/tag';
import { getTagRoute } from '@/shared/lib/tags';
import { formatBytes } from '@/shared/lib/utils';
import type { LineageNodeData } from '@/shared/types/lineageNode';
import {
  DrawerDescription,
  DrawerDomainList,
  DrawerEmptyValue,
  DrawerOwnerList,
  DrawerSchemaList,
} from '@/shared/ui/AssetMetadata';
import { BadgeList } from '@/shared/ui/BadgeList';
import type { DrawerSection, InfoRow } from '@/shared/ui/lineage/LineageNodeDrawer';

import { useDisplaySettingsStore } from '@/entities/display-settings';
import { filterEntityLabels } from '@/entities/search';

import { useColumnData } from './useColumnData';

/**
 * entityType에 따라 LineageNodeDrawer의 데이터를 생성하는 훅
 */
export const useLineageNodeDrawer = (data: LineageNodeData | null, open: boolean) => {
  const showOwners = useDisplaySettingsStore((state) => state.showOwners);
  const navigate = useNavigate();
  const shouldFetchColumns = data?.entityType === ASSET_TYPES.TABLE;
  const { data: columnResponse, isLoading } = useColumnData(
    shouldFetchColumns ? data.fullyQualifiedName : undefined,
    open && shouldFetchColumns
  );

  const columns = useMemo(() => columnResponse?.data || [], [columnResponse?.data]);

  const infoRows = useMemo((): InfoRow[] => {
    if (!data) return [];

    const entityType = data.entityType;

    if (entityType === ASSET_TYPES.MLMODEL) {
      return [
        {
          label: '알고리즘',
          value: data.entityType || <DrawerEmptyValue>-</DrawerEmptyValue>,
        },
        {
          label: '대상',
          value: <DrawerEmptyValue>-</DrawerEmptyValue>,
        },
        {
          label: '서버',
          value: <DrawerEmptyValue>-</DrawerEmptyValue>,
        },
        {
          label: '대시보드',
          value: <DrawerEmptyValue>-</DrawerEmptyValue>,
        },
      ];
    }

    if (entityType === ASSET_TYPES.CONTAINER) {
      return [
        {
          label: '객체',
          value: data.numberOfObjects !== undefined ? data.numberOfObjects : '-',
        },
        {
          label: '서비스 유형',
          value: data.serviceType || <DrawerEmptyValue>-</DrawerEmptyValue>,
        },
        {
          label: '컬럼',
          value: <DrawerEmptyValue>-</DrawerEmptyValue>,
        },
      ];
    }

    if (entityType === ASSET_TYPES.TOPIC) {
      return [
        {
          label: '파티션',
          value: data.partitions !== undefined ? data.partitions : '-',
        },
        {
          label: '복제 계수',
          value: data.replicationFactor !== undefined ? data.replicationFactor : '-',
        },
        {
          label: '보존 크기',
          value: 'N/A',
        },
        {
          label: '정리 정책',
          value: data.cleanupPolicies && data.cleanupPolicies.length > 0 ? data.cleanupPolicies.join(', ') : '-',
        },
        {
          label: '최대 메시지 크기',
          value: formatBytes(data.maximumMessageSize),
        },
        {
          label: '스키마 유형',
          value: data.messageSchema?.schemaType || '-',
        },
      ];
    }

    return [
      {
        label: '컬럼',
        value: isLoading ? '...' : columns.length > 0 ? columns.length : '-',
      },
    ];
  }, [data, columns, isLoading]);

  const sections = useMemo((): DrawerSection[] => {
    if (!data) return [];

    const result: DrawerSection[] = [];

    if (showOwners) {
      result.push({
        id: 'owners',
        title: '소유자',
        content:
          data.owners && data.owners.length > 0 ? (
            <DrawerOwnerList owners={data.owners} />
          ) : (
            <DrawerEmptyValue>소유자 없음</DrawerEmptyValue>
          ),
      });
    }

    result.push({
      id: 'domains',
      title: '도메인',
      content:
        data.domains && data.domains.length > 0 ? (
          <DrawerDomainList
            domains={data.domains.map((d) => ({
              id: d.id,
              name: d.name,
              displayName: d.displayName,
              fullyQualifiedName: d.fullyQualifiedName,
            }))}
          />
        ) : (
          <DrawerEmptyValue>연결된 도메인이 없습니다</DrawerEmptyValue>
        ),
    });

    const classificationTags = mapTagsToBasicInfo(
      filterEntityLabels(data.tags, {
        includeOnlySource: 'Classification',
        requireConfirmed: true,
      })
    );

    const allTags = [
      ...classificationTags,
      ...(data.tier
        ? [
            {
              name: data.tier.name,
              displayName: data.tier.displayName || data.tier.name,
              tagFQN: data.tier.tagFQN,
            },
          ]
        : []),
    ];

    const uniqueTags = allTags.filter((tag, index, self) => index === self.findIndex((t) => t.name === tag.name));

    const hasTags = uniqueTags.length > 0;
    result.push({
      id: 'tags',
      title: '태그',
      content: hasTags ? (
        <BadgeList
          items={uniqueTags}
          onItemClick={(tagFQN) => navigate(getTagRoute(tagFQN))}
          iconName="tag"
          isEllipsis={false}
        />
      ) : (
        <DrawerEmptyValue>연결된 태그가 없습니다</DrawerEmptyValue>
      ),
    });

    result.push({
      id: 'description',
      title: '설명',
      content: data.description ? (
        <DrawerDescription description={data.description} />
      ) : (
        <DrawerEmptyValue>데이터를 찾을 수 없습니다</DrawerEmptyValue>
      ),
    });

    if (data.entityType === ASSET_TYPES.MLMODEL) {
      result.push({
        id: 'features',
        title: '기능',
        content: <DrawerEmptyValue>사용 가능한 기능 데이터가 없습니다</DrawerEmptyValue>,
      });
    }

    if (columns.length > 0) {
      result.push({
        id: 'schema',
        title: '스키마',
        content: <DrawerSchemaList columns={columns} isLoading={isLoading} />,
      });
    }

    return result;
  }, [data, columns, isLoading, showOwners, navigate]);

  return {
    infoRows,
    sections,
  };
};
